import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PendingInline } from "@/components/ui/pending-indicator";
import {
  buildCommandItems,
  filterCommandItems,
  type CommandItem,
} from "./command-math";
import {
  recentToCommandItems,
  searchDeskCommandResults,
} from "./desk-command-search";
import { loadRecentDocs } from "./recent-docs";
import {
  applyLockToken,
  scopeTokenFromPathname,
  shouldClearTokenOnBackspace,
  tokenChipLabel,
  type SearchToken,
} from "./search-token-math";

const SEARCH_DEBOUNCE_MS = 280;

/** ⌘K / Ctrl+K Desk jump + create + Recent/Results + search tokens — plan/36 · plan/35 */
export function CommandMenu(props: {
  companySlug: string;
  held: readonly string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { companySlug, held, open, onOpenChange } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [recentTick, setRecentTick] = useState(0);
  const [results, setResults] = useState<CommandItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [tokens, setTokens] = useState<SearchToken[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasToken = tokens.length > 0;

  const catalog = useMemo(
    () => buildCommandItems(companySlug, held),
    [companySlug, held],
  );

  const recent = useMemo(() => {
    void recentTick;
    return recentToCommandItems(companySlug, loadRecentDocs(companySlug));
  }, [companySlug, recentTick]);

  const qTrim = query.trim();
  const staticFiltered = useMemo(
    () => filterCommandItems(catalog, qTrim),
    [catalog, qTrim],
  );

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    setResults([]);
    setSearching(false);
    // Pre-lock object/queue scope when opened from that list — plan/35
    const pre = scopeTokenFromPathname(location.pathname, companySlug);
    setTokens(pre ? [pre] : []);
    setRecentTick((n) => n + 1);
    const t = window.setTimeout(() => inputRef.current?.focus(), 10);
    return () => window.clearTimeout(t);
  }, [open, companySlug, location.pathname]);

  useEffect(() => {
    setActive(0);
  }, [query, results, tokens]);

  useEffect(() => {
    if (!open) return;
    // Token lock: scoped results with empty query; scope locks from 1 char
    if (!hasToken && qTrim.length < 1) {
      setResults([]);
      setSearching(false);
      return;
    }
    const ac = new AbortController();
    setSearching(true);
    const t = window.setTimeout(() => {
      void searchDeskCommandResults({
        companySlug,
        held,
        query: qTrim,
        signal: ac.signal,
        tokens,
      })
        .then((hits) => {
          if (!ac.signal.aborted) setResults(hits);
        })
        .catch(() => {
          if (!ac.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!ac.signal.aborted) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      ac.abort();
      window.clearTimeout(t);
    };
  }, [open, qTrim, companySlug, held, tokens, hasToken]);

  const go = staticFiltered.filter((i) => i.group === "Go");
  const create = staticFiltered.filter((i) => i.group === "Create");
  const showRecent = !hasToken && qTrim.length === 0 && recent.length > 0;
  const showResults = hasToken || qTrim.length >= 1;

  const flat = [
    ...(showRecent ? recent : []),
    ...(showResults ? results : []),
    ...(!hasToken ? go : []),
    ...(!hasToken ? create : []),
  ];

  function run(item: CommandItem) {
    const action = item.action;
    if (
      action?.kind === "lockParty" ||
      action?.kind === "lockProduct" ||
      action?.kind === "lockObject" ||
      action?.kind === "lockQueue"
    ) {
      setTokens((prev) => applyLockToken(prev, action.token));
      setQuery("");
      setResults([]);
      setActive(0);
      window.setTimeout(() => inputRef.current?.focus(), 10);
      return;
    }
    onOpenChange(false);
    navigate(item.path);
  }

  let offset = 0;
  const recentOffset = 0;
  if (showRecent) offset += recent.length;
  const resultsOffset = offset;
  if (showResults) offset += results.length;
  const goOffset = offset;
  if (!hasToken) offset += go.length;
  const createOffset = offset;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[min(100%-1.5rem,28rem)] gap-0 overflow-hidden p-0"
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, Math.max(0, flat.length - 1)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const item = flat[active];
            if (item) run(item);
          }
        }}
      >
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">
          Search documents, lock a customer, product, or list scope, jump, or create
        </DialogDescription>
        <div className="border-b border-[var(--color-border)] px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            {tokens.map((tok, idx) => (
              <button
                key={`${tok.type}-${idx}`}
                type="button"
                className="inline-flex max-w-full items-center gap-1 rounded-lg bg-[var(--color-background)] px-2 py-1 text-xs font-medium text-[var(--color-foreground)]"
                onClick={() => {
                  setTokens((prev) => prev.filter((_, i) => i !== idx));
                  setQuery("");
                  setResults([]);
                  inputRef.current?.focus();
                }}
                title="Remove token"
              >
                <span className="truncate">{tokenChipLabel(tok)}</span>
                <span className="opacity-60" aria-hidden>
                  ×
                </span>
              </button>
            ))}
            <Input
              ref={inputRef}
              className="min-w-[8rem] flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
              placeholder={
                hasToken
                  ? "Filter within… stack a queue, or Enter"
                  : "Search docs, lock a customer…"
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (
                  hasToken &&
                  shouldClearTokenOnBackspace(query, e.key)
                ) {
                  e.preventDefault();
                  setTokens((prev) => prev.slice(0, -1));
                  setResults([]);
                }
              }}
            />
          </div>
        </div>
        <div className="max-h-[min(60vh,22rem)] overflow-y-auto p-2">
          {flat.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-[var(--color-muted)]">
              {searching ? (
                <PendingInline className="justify-center">Searching…</PendingInline>
              ) : !hasToken && qTrim.length === 1 ? (
                "Type a scope keyword (so, pay…) or 2+ chars"
              ) : (
                "No matches"
              )}
            </p>
          ) : (
            <>
              {showRecent ? (
                <CommandGroup
                  title="Recent"
                  items={recent}
                  activeId={flat[active]?.id}
                  offset={recentOffset}
                  onRun={run}
                  onHover={(id) => {
                    const i = flat.findIndex((x) => x.id === id);
                    if (i >= 0) setActive(i);
                  }}
                />
              ) : null}
              {showResults && results.length > 0 ? (
                <CommandGroup
                  title={hasToken ? "In scope" : "Results"}
                  items={results}
                  activeId={flat[active]?.id}
                  offset={resultsOffset}
                  onRun={run}
                  onHover={(id) => {
                    const i = flat.findIndex((x) => x.id === id);
                    if (i >= 0) setActive(i);
                  }}
                />
              ) : null}
              {showResults && searching && results.length === 0 ? (
                <p className="px-2.5 py-2 text-xs text-[var(--color-muted)]">
                  <PendingInline>Searching…</PendingInline>
                </p>
              ) : null}
              {!hasToken && go.length > 0 && (
                <CommandGroup
                  title="Go"
                  items={go}
                  activeId={flat[active]?.id}
                  offset={goOffset}
                  onRun={run}
                  onHover={(id) => {
                    const i = flat.findIndex((x) => x.id === id);
                    if (i >= 0) setActive(i);
                  }}
                />
              )}
              {!hasToken && create.length > 0 && (
                <CommandGroup
                  title="Create"
                  items={create}
                  activeId={flat[active]?.id}
                  offset={createOffset}
                  onRun={run}
                  onHover={(id) => {
                    const i = flat.findIndex((x) => x.id === id);
                    if (i >= 0) setActive(i);
                  }}
                />
              )}
            </>
          )}
        </div>
        <p className="border-t border-[var(--color-border)] px-3 py-2 text-[11px] text-[var(--color-muted)]">
          {hasToken
            ? "↑↓ · Enter opens list · ⌫ clears last chip · Esc"
            : "↑↓ · Enter lock customer / open · Esc"}
        </p>
      </DialogContent>
    </Dialog>
  );
}

function CommandGroup(props: {
  title: string;
  items: CommandItem[];
  activeId?: string;
  offset: number;
  onRun: (item: CommandItem) => void;
  onHover: (id: string) => void;
}) {
  return (
    <div className="mb-2">
      <p className="m-0 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-fg)]">
        {props.title}
      </p>
      <ul className="m-0 list-none space-y-0.5 p-0">
        {props.items.map((item) => {
          const active = item.id === props.activeId;
          return (
            <li key={item.id}>
              <button
                type="button"
                className={[
                  "flex w-full items-center justify-between gap-3 rounded-xl px-2.5 py-2.5 text-left text-sm",
                  active
                    ? "bg-[var(--color-foreground)] text-[var(--color-card)]"
                    : "text-[var(--color-foreground)] hover:bg-[var(--color-background)]",
                ].join(" ")}
                onMouseEnter={() => props.onHover(item.id)}
                onClick={() => props.onRun(item)}
              >
                <span className="font-medium">{item.label}</span>
                {item.hint ? (
                  <span
                    className={
                      active
                        ? "text-[11px] opacity-70"
                        : "text-[11px] text-[var(--color-muted)]"
                    }
                  >
                    {item.hint}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function useCommandMenuHotkey(opts: {
  enabled: boolean;
  onOpen: () => void;
}) {
  useEffect(() => {
    if (!opts.enabled) return;
    function onKey(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "k") return;
      e.preventDefault();
      opts.onOpen();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opts.enabled, opts.onOpen]);
}
